import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { BUILDWISE_MODULES } from 'components/drawer/constants';
import { ConfigEntryFeatureType } from 'config/config';
import useUserData from 'hooks/useUserData';
import pluralize, { singular } from 'pluralize';
import { useMemo } from 'react';

const ARTICLE_BUTTONS: Record<string, ConfigEntryFeatureType> = {
  'Share Knowledge': {
    displayName: 'Share Knowledge',
    name: 'Share Knowledge',
    isActive: true,
  },
  'Ask Peers': {
    displayName: 'Ask Peers',
    name: 'Ask Peers',
    isActive: true,
  },
  'Suggest Improvements': {
    displayName: 'Suggest Improvements',
    name: 'Suggest Improvements',
    isActive: true,
  },
  'Request a Post': {
    displayName: 'Request a Post',
    name: 'Request a Post',
    isActive: true,
  },
  'Chat with AI': {
    displayName: 'Chat with AI',
    name: 'Chat with AI',
    isActive: true,
  },
  'Summarize Search Results': {
    displayName: 'Summarize these posts',
    name: 'Summarize Search Results',
    isActive: true,
  },
};

function useBuildWiseModules() {
  const { companyId } = useUserData();
  const {
    activeUniqueEntries,
    entries: buildwiseModules,
    config: buildwiseModulesConfig,
    isLoading: isLoadingBuildWiseModules,
    isFetched: isFetchedBuildWiseModules,
    updateConfigCache,
  } = useConfigQuery({
    companyId,
    configName: CONFIGS.BUILDWISE_MODULES.configName,
  });
  const {
    homeModule,
    homeModuleName,
    homeModuleFeatures,
    partnersModuleName,
    projectsModuleName,
    proceduresModuleName,
    insightsModuleName,
  } = useMemo(() => {
    const homeModule = activeUniqueEntries.find(
      (module) => module.entryCode === BUILDWISE_MODULES.HOME,
    );
    const homeModuleFeatures: Record<string, ConfigEntryFeatureType> = {
      ...ARTICLE_BUTTONS,
    };
    homeModule?.referenceObject?.features?.forEach(({ name, ...item }) => {
      if (homeModuleFeatures[name]) {
        homeModuleFeatures[name] = {
          ...homeModuleFeatures[name],
          ...item,
        };
      }
    });

    return {
      homeModule,
      homeModuleName: homeModule?.entryDescription || BUILDWISE_MODULES.HOME,
      homeModuleFeatures,
      partnersModuleName:
        activeUniqueEntries.find((module) => module.entryCode === BUILDWISE_MODULES.PARTNERS)
          ?.entryDescription || BUILDWISE_MODULES.PARTNERS,
      projectsModuleName:
        activeUniqueEntries.find((module) => module.entryCode === BUILDWISE_MODULES.PROJECTS)
          ?.entryDescription || BUILDWISE_MODULES.PROJECTS,
      proceduresModuleName:
        activeUniqueEntries.find((module) => module.entryCode === BUILDWISE_MODULES.PROCEDURES)
          ?.entryDescription || BUILDWISE_MODULES.PROCEDURES,
      insightsModuleName:
        activeUniqueEntries.find((module) => module.entryCode === BUILDWISE_MODULES.INSIGHTS)
          ?.entryDescription || BUILDWISE_MODULES.INSIGHTS,
    };
  }, [activeUniqueEntries]);

  return {
    buildwiseModules,
    buildwiseModulesConfig,
    homeModule,
    homeModuleName,
    homeModuleFeatures,
    isFetchedBuildWiseModules,
    isLoadingBuildWiseModules,
    singularHomeModuleName: singular(homeModuleName),
    pluralHomeModuleName: pluralize(homeModuleName),
    projectsModuleName,
    singularProjectsModuleName: singular(projectsModuleName),
    pluralProjectsModuleName: pluralize(projectsModuleName),
    partnersModuleName,
    singularPartnersModuleName: singular(partnersModuleName),
    pluralPartnersModuleName: pluralize(partnersModuleName),
    proceduresModuleName,
    singularProceduresModuleName: singular(proceduresModuleName),
    pluralProceduresModuleName: pluralize(proceduresModuleName),
    insightsModuleName,
    singularInsightsModuleName: singular(insightsModuleName),
    pluralInsightsModuleName: pluralize(insightsModuleName),
    updateConfigCache,
  };
}

export default useBuildWiseModules;
